/* istanbul ignore file */
import { createAction } from '@reduxjs/toolkit';
import type {
    IDocument,
    IFetchDocumentsRequest,
    IFetchVaultsRequest,
    IMediaTypeRead,
    ISecureDocumentStoreSettings,
    ITag,
    IUpdateDocumentAttributesRequest,
    IUploadDocumentRequest,
    IUploadMultipleDocumentRequest,
    IVault,
} from '../../api/domain';

enum SecureDocumentStoreApiActions {
    FETCH_DOCUMENT = 'sds.api.fetch.document',
    FETCH_DOCUMENT_SUCCESS = 'sds.api.fetch.document.success',
    FETCH_DOCUMENT_FAILURE = 'sds.api.fetch.document.failure',

    FETCH_DOCUMENTS_BY_IDS = 'sds.api.fetch.documents-by-ids',
    FETCH_DOCUMENTS_BY_IDS_SUCCESS = 'sds.api.fetch.documents-by-ids.success',
    FETCH_DOCUMENTS_BY_IDS_FAILURE = 'sds.api.fetch.documents-by-ids.failure',

    FETCH_UNREAD_DOCUMENT_COUNT = 'sds.api.fetch.unread-document-count',
    FETCH_UNREAD_DOCUMENT_COUNT_SUCCESS = 'sds.api.fetch.unread-document-count.success',
    FETCH_UNREAD_DOCUMENT_COUNT_FAILURE = 'sds.api.fetch.unread-document-count.failure',

    FETCH_TOTAL_AMOUNT_DOCUMENTS_SUCCESS = 'sds.api.fetch.total-amount-documents.success',

    DELETE_DOCUMENT = 'sds.api.delete.document',
    DELETE_DOCUMENT_SUCCESS = 'sds.api.delete.document.success',
    DELETE_DOCUMENT_FAILURE = 'sds.api.delete.document.failure',

    DELETE_MULTIPLE_DOCUMENTS = 'sds.api.delete.multiple-document',
    DELETE_MULTIPLE_DOCUMENTS_SUCCESS = 'sds.api.delete.multiple-document.success',
    DELETE_MULTIPLE_DOCUMENTS_FAILURE = 'sds.api.delete.multiple-document.failure',

    FETCH_ALL_DOCUMENTS_THROTTLED = 'sds.api.fetch.documents.throttled',
    FETCH_ALL_DOCUMENTS = 'sds.api.fetch.documents',
    FETCH_ALL_DOCUMENTS_SUCCESS = 'sds.api.fetch.documents.success',
    FETCH_ALL_DOCUMENTS_FAILURE = 'sds.api.fetch.documents.failure',

    FETCH_VAULTS = 'sds.api.fetch.vaults',
    FETCH_VAULTS_SUCCESS = 'sds.api.fetch.vaults.success',
    FETCH_VAULTS_FAILURE = 'sds.api.fetch.vaults.failure',

    FETCH_SETTINGS = 'sds.api.fetch.settings',
    FETCH_SETTINGS_SUCCESS = 'sds.api.fetch.settings.success',
    FETCH_SETTINGS_FAILURE = 'sds.api.fetch.settings.failure',

    FETCH_TAGS = 'sds.api.fetch.tags',
    FETCH_TAGS_SUCCESS = 'sds.api.fetch.tags.success',
    FETCH_TAGS_FAILURE = 'sds.api.fetch.tags.failure',

    FETCH_MEDIA_TYPES = 'sds.api.fetch.media_types',
    FETCH_MEDIA_TYPES_SUCCESS = 'sds.api.fetch.media_types.success',
    FETCH_MEDIA_TYPES_FAILURE = 'sds.api.fetch.media_types.failure',

    UPLOAD_DOCUMENT = 'sds.api.upload.document',
    UPLOAD_DOCUMENT_SUCCESS = 'sds.api.upload.document.success',
    UPLOAD_DOCUMENT_FAILURE = 'sds.api.upload.document.failure',

    DOWNLOAD_DOCUMENT = 'sds.api.download.document',
    DOWNLOAD_DOCUMENT_SUCCESS = 'sds.api.download.document.success',
    DOWNLOAD_DOCUMENT_FAILURE = 'sds.api.download.document.failure',

    UPDATE_DOCUMENT = 'sds.api.update.document',
    UPDATE_DOCUMENT_SUCCESS = 'sds.api.update.document.success',
    UPDATE_DOCUMENT_FAILURE = 'sds.api.update.document.failure',

    UPLOAD_DOCUMENTS = 'sds.api.upload.documents',
    UPLOAD_DOCUMENTS_SUCCESS = 'sds.api.upload.documents.success',
    UPLOAD_DOCUMENTS_FAILURE = 'sds.api.upload.documents.failure',

    UPLOAD_SIMPLE_FILES = 'sds.api.upload.simple-files',
    UPLOAD_SIMPLE_FILES_SUCCESS = 'sds.api.upload.simple-files.success',
    UPLOAD_SIMPLE_FILES_FAILURE = 'sds.api.upload.simple-files.failure',

    MARK_DOCUMENT_AS_READ = 'sds.api.mark-document-as-read',
    MARK_DOCUMENT_AS_READ_SUCCESS = 'sds.api.mark-document-as-read.success',
    MARK_DOCUMENT_AS_READ_FAILURE = 'sds.api.mark-document-as-read.failure',

    MARK_DOCUMENT_AS_UNREAD = 'sds.api.mark-document-as-unread',
    MARK_DOCUMENT_AS_UNREAD_SUCCESS = 'sds.api.mark-document-as-unread.success',
    MARK_DOCUMENT_AS_UNREAD_FAILURE = 'sds.api.mark-document-as-unread.failure',
}

export const fetchDocument = createAction<string>(SecureDocumentStoreApiActions.FETCH_DOCUMENT);
export const fetchDocumentSuccess = createAction<IDocument>(SecureDocumentStoreApiActions.FETCH_DOCUMENT_SUCCESS);
export const fetchDocumentFailure = createAction(SecureDocumentStoreApiActions.FETCH_DOCUMENT_FAILURE);

export const fetchDocumentsByIds = createAction<string[]>(SecureDocumentStoreApiActions.FETCH_DOCUMENTS_BY_IDS);
export const fetchDocumentsByIdsSuccess = createAction<IDocument[]>(SecureDocumentStoreApiActions.FETCH_DOCUMENTS_BY_IDS_SUCCESS);
export const fetchDocumentsByIdsFailure = createAction(SecureDocumentStoreApiActions.FETCH_DOCUMENTS_BY_IDS_FAILURE);

export const fetchUnreadDocumentCount = createAction(SecureDocumentStoreApiActions.FETCH_UNREAD_DOCUMENT_COUNT);
export const fetchUnreadDocumentCountSuccess = createAction<number>(SecureDocumentStoreApiActions.FETCH_UNREAD_DOCUMENT_COUNT_SUCCESS);
export const fetchUnreadDocumentCountFailure = createAction(SecureDocumentStoreApiActions.FETCH_UNREAD_DOCUMENT_COUNT_FAILURE);

export const fetchTotalAmountOfDocumentsSuccess = createAction<number>(SecureDocumentStoreApiActions.FETCH_TOTAL_AMOUNT_DOCUMENTS_SUCCESS);

export const fetchSettings = createAction(SecureDocumentStoreApiActions.FETCH_SETTINGS);
export const fetchSettingsSuccess = createAction<ISecureDocumentStoreSettings>(SecureDocumentStoreApiActions.FETCH_SETTINGS_SUCCESS);
export const fetchSettingsFailure = createAction(SecureDocumentStoreApiActions.FETCH_SETTINGS_FAILURE);

export const fetchTags = createAction(SecureDocumentStoreApiActions.FETCH_TAGS);
export const fetchTagsSuccess = createAction<ITag[]>(SecureDocumentStoreApiActions.FETCH_TAGS_SUCCESS);
export const fetchTagsFailure = createAction(SecureDocumentStoreApiActions.FETCH_TAGS_FAILURE);

export const fetchMediaTypes = createAction(SecureDocumentStoreApiActions.FETCH_MEDIA_TYPES);
export const fetchMediaTypesSuccess = createAction<IMediaTypeRead[]>(SecureDocumentStoreApiActions.FETCH_MEDIA_TYPES_SUCCESS);
export const fetchMediaTypesFailure = createAction(SecureDocumentStoreApiActions.FETCH_MEDIA_TYPES_FAILURE);

export const deleteDocument = createAction<{ documentId: string; request: IFetchDocumentsRequest }>(SecureDocumentStoreApiActions.DELETE_DOCUMENT);
export const deleteDocumentSuccess = createAction(SecureDocumentStoreApiActions.DELETE_DOCUMENT_SUCCESS);
export const deleteDocumentFailure = createAction(SecureDocumentStoreApiActions.DELETE_DOCUMENT_FAILURE);

export const deleteMultipleDocuments = createAction<{ documentIds: string[]; request: IFetchDocumentsRequest }>(
    SecureDocumentStoreApiActions.DELETE_MULTIPLE_DOCUMENTS,
);
export const deleteMultipleDocumentsSuccess = createAction(SecureDocumentStoreApiActions.DELETE_MULTIPLE_DOCUMENTS_SUCCESS);
export const deleteMultipleDocumentsFailure = createAction(SecureDocumentStoreApiActions.DELETE_MULTIPLE_DOCUMENTS_FAILURE);

export const updateDocument = createAction<{ request: IUpdateDocumentAttributesRequest; showNotification: boolean }>(
    SecureDocumentStoreApiActions.UPDATE_DOCUMENT,
);
export const updateDocumentSuccess = createAction(SecureDocumentStoreApiActions.UPDATE_DOCUMENT_SUCCESS);
export const updateDocumentFailure = createAction(SecureDocumentStoreApiActions.UPDATE_DOCUMENT_FAILURE);

export const fetchAllDocumentsThrottled = createAction<{ request: IFetchDocumentsRequest; hideProgressIndicator: boolean }>(
    SecureDocumentStoreApiActions.FETCH_ALL_DOCUMENTS_THROTTLED,
);
export const fetchAllDocuments = createAction<{ request: IFetchDocumentsRequest; hideProgressIndicator: boolean }>(
    SecureDocumentStoreApiActions.FETCH_ALL_DOCUMENTS,
);
export const fetchAllDocumentsSuccess = createAction<IDocument[]>(SecureDocumentStoreApiActions.FETCH_ALL_DOCUMENTS_SUCCESS);
export const fetchAllDocumentsFailure = createAction(SecureDocumentStoreApiActions.FETCH_ALL_DOCUMENTS_FAILURE);

export const fetchVaults = createAction<IFetchVaultsRequest>(SecureDocumentStoreApiActions.FETCH_VAULTS);
export const fetchVaultsSuccess = createAction<IVault[]>(SecureDocumentStoreApiActions.FETCH_VAULTS_SUCCESS);
export const fetchVaultsFailure = createAction(SecureDocumentStoreApiActions.FETCH_VAULTS_FAILURE);

export const uploadDocument = createAction<IUploadDocumentRequest>(SecureDocumentStoreApiActions.UPLOAD_DOCUMENT);
export const uploadDocumentSuccess = createAction<string>(SecureDocumentStoreApiActions.UPLOAD_DOCUMENT_SUCCESS);
export const uploadDocumentFailure = createAction(SecureDocumentStoreApiActions.UPLOAD_DOCUMENT_FAILURE);

export const downloadDocument = createAction<string>(SecureDocumentStoreApiActions.DOWNLOAD_DOCUMENT);
export const downloadDocumentSuccess = createAction(SecureDocumentStoreApiActions.DOWNLOAD_DOCUMENT_SUCCESS);
export const downloadDocumentFailure = createAction(SecureDocumentStoreApiActions.DOWNLOAD_DOCUMENT_FAILURE);

export const uploadDocuments = createAction<IUploadMultipleDocumentRequest>(SecureDocumentStoreApiActions.UPLOAD_DOCUMENTS);
export const uploadDocumentsSuccess = createAction<string[]>(SecureDocumentStoreApiActions.UPLOAD_DOCUMENTS_SUCCESS);
export const uploadDocumentsFailure = createAction(SecureDocumentStoreApiActions.UPLOAD_DOCUMENTS_FAILURE);

export const uploadSimpleFiles = createAction<{
    files: File[];
    vaultId: string;
    request: IFetchDocumentsRequest;
}>(SecureDocumentStoreApiActions.UPLOAD_SIMPLE_FILES);
export const uploadSimpleFilesSuccess = createAction<string[]>(SecureDocumentStoreApiActions.UPLOAD_SIMPLE_FILES_SUCCESS);
export const uploadSimpleFilesFailure = createAction(SecureDocumentStoreApiActions.UPLOAD_SIMPLE_FILES_FAILURE);

export const markDocumentAsRead = createAction<string>(SecureDocumentStoreApiActions.MARK_DOCUMENT_AS_READ);
export const markDocumentAsReadSuccess = createAction(SecureDocumentStoreApiActions.MARK_DOCUMENT_AS_READ_SUCCESS);
export const markDocumentAsReadFailure = createAction(SecureDocumentStoreApiActions.MARK_DOCUMENT_AS_READ_FAILURE);

export const markDocumentAsUnread = createAction<string>(SecureDocumentStoreApiActions.MARK_DOCUMENT_AS_UNREAD);
export const markDocumentAsUnreadSuccess = createAction(SecureDocumentStoreApiActions.MARK_DOCUMENT_AS_UNREAD_SUCCESS);
export const markDocumentAsUnreadFailure = createAction(SecureDocumentStoreApiActions.MARK_DOCUMENT_AS_UNREAD_FAILURE);
